import { useEffect, useEffectEvent, useRef } from 'react';
import { toast } from 'sonner';

import type { ExtractionRunwayState } from '@/hooks/use-extraction-runway';
import { formatTrialDate } from '@/lib/trial-phase';

const STORAGE_PREFIX = 'contract-analyzer:trial-ended-notice';

export function trialEndedNoticeKey(tenantId: string, userId: string, endedAt: Date): string {
  return `${STORAGE_PREFIX}:${tenantId}:${userId}:${endedAt.toISOString()}`;
}

function hasSeen(key: string): boolean {
  try {
    return window.localStorage.getItem(key) !== null;
  } catch {
    return true;
  }
}

function markSeen(key: string): void {
  try {
    window.localStorage.setItem(key, new Date().toISOString());
  } catch {
    // The mounted hook retains the acknowledgement when storage is unavailable.
  }
}

/** How the acknowledgement reaches the person; the app wires the toast. */
export type TrialEndedAnnouncer = (title: string, description: string) => void;

const announceWithToast: TrialEndedAnnouncer = (title, description) => {
  toast(title, { description });
};

/**
 * Acknowledges the moment a trial ends, once per person per trial, so the
 * sidebar rail changing shape is explained rather than left to be noticed.
 * No action in the toast: the rail and the Billing tab carry the way forward.
 */
export function useTrialEndedNotice({
  state,
  tenantId,
  userId,
  now,
  announce = announceWithToast,
}: {
  readonly state: ExtractionRunwayState;
  readonly tenantId: string | null;
  readonly userId: string | null;
  /** The clock the notice is dated against; the wall clock when unset. */
  readonly now?: Date | undefined;
  readonly announce?: TrialEndedAnnouncer;
}): void {
  const endedAt = state.status === 'ready' && state.trial.kind === 'ended' ? state.trial.endedAt : null;
  const endedAtIso = endedAt?.toISOString() ?? null;
  const acknowledged = useRef(new Set<string>());
  const announceEndedTrial = useEffectEvent((date: Date) => {
    announce(
      `Your trial ended on ${formatTrialDate(date, now ?? new Date())}.`,
      'Results stay available.',
    );
  });

  useEffect(() => {
    if (endedAtIso === null || tenantId === null || userId === null) return;
    const key = trialEndedNoticeKey(tenantId, userId, new Date(endedAtIso));
    if (acknowledged.current.has(key) || hasSeen(key)) return;
    acknowledged.current.add(key);
    markSeen(key);
    announceEndedTrial(new Date(endedAtIso));
  }, [endedAtIso, tenantId, userId]);
}
